import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link } from "react-router-dom"
import { AgencyOperationsAdminVenueTabs } from "src/components/agency/operations/administrator/venues/AgencyOperationsAdminVenueTabs"
import { CreateAgencyOperationsAdminVenueUserAction, UpdateAgencyOperationsAdminVenueUserAction } from "src/components/agency/operations/administrator/venues/users/AgencyOperationsAdminVenueUserActions"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { UserFragment, User_Status_Enum, Users_Bool_Exp } from "src/state/gql/_types"
import { capitalizeWord } from "src/utils/string"

export function AgencyOperationsAdminVenueUsers() {

    const { currentItemId: currentTenantId } = useAppState(state => state.tenants)
    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.users)
    const { 
        subscribeToItems, 
        unsubscribeFromItems, 
        getMoreItems, 
        resetModule, 
        setMeta,
        selectItem
    } = useActions().users
    const { view } = useAppState(state => state.ui)

    const {
        filters,
        filterParams
    } = usePageFilters<Users_Bool_Exp['_and'], UserFragment>({
        baseFilters: [{
            tenant_id: { _eq: currentTenantId }
        }],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: User_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: User_Status_Enum.Active } }] },
            invited: { label: 'Invited', filter: [{ status: { _eq: User_Status_Enum.Invited } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: User_Status_Enum.Inactive } }] },
            all: { label: 'All', filter: [] }
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<UserFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor('user_number', {
            header: '#'
        }),
        columnHelper.accessor('type', {
            header: 'Department',
            cell: row => capitalizeWord(row.getValue())
        }),
        columnHelper.accessor('role', {
            header: 'Role',
            cell: row => capitalizeWord(row.getValue())
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: row => capitalizeWord(row.getValue())
        }),
        columnHelper.accessor('email', {
            header: 'Email'
        }),
        columnHelper.accessor('first_name', {
            header: 'First Name'
        }),
        columnHelper.accessor('last_name', {
            header: 'Last Name'
        }),
        columnHelper.accessor('city', {
            header: 'City'
        }),
        columnHelper.accessor('state', {
            header: 'State'
        }),
        columnHelper.accessor('phone', {
            header: 'Phone'
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<UserFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        selectItem(id)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/users/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.status}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<UserFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Users`}
                    renderAction={
                        <Group>
                            <UpdateAgencyOperationsAdminVenueUserAction />
                            <CreateAgencyOperationsAdminVenueUserAction/>
                        </Group>
                    }
                />
            }
        >
            <AgencyOperationsAdminVenueTabs/>
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Users' />)}
        </PageLayout>
    )

}